import type { LayerTreeItem } from './types';
import type { CanvasGroup } from '../../types';
import type { CanvasEntity } from '../../entityTypes';
import { getTextBlockData, getTextLabelData, getSpriteData, getAdrTriggerData, type GroupData, type TaskData } from '../../entityTypes';
import type { GlobalTask } from '../../../types';
import { getAdrById } from '../../../adr/adrStore';
import i18n from '../../../i18n';

// ============================================
// Parent Maps (computed once, shared across layers)
// ============================================

export interface ParentMaps {
  nodeParentMap: Map<string, string>;  // nodeId -> groupId
  groupParentMap: Map<string, string>; // groupId -> parentGroupId
}

export function buildParentMaps(groups: CanvasGroup[]): ParentMaps {
  const nodeParentMap = new Map<string, string>();
  const groupParentMap = new Map<string, string>();
  for (const g of groups) {
    for (const nid of g.memberNodeIds) nodeParentMap.set(nid, g.id);
    for (const cid of g.memberGroupIds) groupParentMap.set(cid, g.id);
  }
  return { nodeParentMap, groupParentMap };
}

// ============================================
// Tree Builder
// ============================================

export function buildLayerTree(
  layerId: string,
  entities: CanvasEntity[],
  globalTasksById: Record<string, GlobalTask>,
  groups: CanvasGroup[],
  parentMaps: ParentMaps,
  topLevelEntityOrder: string[],
): LayerTreeItem[] {
  const { nodeParentMap, groupParentMap } = parentMaps;

  // Unified entity info map (nodes, textblocks, textlabels)
  const entityInfo = new Map<string, { kind: LayerTreeItem['kind']; label: string; layerId: string; adrIds?: string[] }>();
  for (const e of entities) {
    if (e.kind === 'task') {
      const task = globalTasksById[e.id];
      if (task) entityInfo.set(e.id, { kind: 'node', label: task.title, layerId: e.layerId });
    } else if (e.kind === 'textblock') {
      const d = getTextBlockData(e);
      entityInfo.set(e.id, { kind: 'textblock', label: d.text.slice(0, 30) || 'TextBlock', layerId: e.layerId });
    } else if (e.kind === 'textlabel') {
      const d = getTextLabelData(e);
      entityInfo.set(e.id, { kind: 'textlabel', label: d.text.slice(0, 30) || 'Label', layerId: e.layerId });
    } else if (e.kind === 'sprite') {
      const d = getSpriteData(e);
      entityInfo.set(e.id, { kind: 'sprite', label: d.label.slice(0, 30) || 'Sprite', layerId: e.layerId });
    } else if (e.kind === 'image') {
      entityInfo.set(e.id, { kind: 'image', label: i18n.t('canvasLayer.image' as any), layerId: e.layerId });
    } else if (e.kind === 'blocknode') {
      entityInfo.set(e.id, { kind: 'blocknode', label: 'BlockNode', layerId: e.layerId });
    } else if (e.kind === 'shape') {
      entityInfo.set(e.id, { kind: 'shape', label: 'Shape', layerId: e.layerId });
    } else if (e.kind === 'adr-trigger') {
      const ad = getAdrTriggerData(e);
      const adrTitle = ad.adrIds.length === 1 ? getAdrById(ad.adrIds[0])?.title : undefined;
      const label = adrTitle || (ad.adrIds.length > 1 ? `${ad.adrIds.length} ADRs` : i18n.t('adr.trigger.label' as any));
      entityInfo.set(e.id, { kind: 'adr-trigger', label, layerId: e.layerId, adrIds: ad.adrIds });
    }
  }

  function buildGroupItem(group: CanvasGroup): LayerTreeItem {
    const children: LayerTreeItem[] = [];
    const groupIdSet = new Set(group.memberGroupIds);
    // Iterate memberEntityIds to preserve interleaved order
    for (const eid of group.memberEntityIds) {
      if (groupIdSet.has(eid)) {
        const childGroup = groups.find(g => g.id === eid);
        if (childGroup) children.push(buildGroupItem(childGroup));
      } else {
        const info = entityInfo.get(eid);
        if (info) children.push({ kind: info.kind, id: eid, label: info.label, layerId: info.layerId, adrIds: info.adrIds });
      }
    }
    return {
      kind: 'group',
      id: group.id,
      label: group.label,
      layerId: group.layerId,
      children,
      collapsed: group.collapsed,
    };
  }

  const items: LayerTreeItem[] = [];
  const groupMap = new Map(groups.map(g => [g.id, g]));

  for (const id of topLevelEntityOrder) {
    const group = groupMap.get(id);
    if (group) {
      if (group.layerId !== layerId) continue;
      if (groupParentMap.has(id)) continue;
      items.push(buildGroupItem(group));
    } else {
      const info = entityInfo.get(id);
      if (!info || info.layerId !== layerId) continue;
      if (nodeParentMap.has(id)) continue;
      items.push({ kind: info.kind, id, label: info.label, layerId: info.layerId, adrIds: info.adrIds });
    }
  }

  return items;
}

// ============================================
// Layer Panel Data Materialization (structural only, no positions)
// ============================================

export function materializeForLayerPanel(
  rawEntities: CanvasEntity[],
  globalTasksById: Record<string, GlobalTask>,
): { entities: CanvasEntity[]; groups: CanvasGroup[]; topLevelEntityOrder: string[] } {
  const validEntityIds = new Set<string>();
  for (const e of rawEntities) {
    switch (e.kind) {
      case 'task': {
        const td = e.data as unknown as TaskData;
        if (globalTasksById[td.taskId]) validEntityIds.add(td.taskId);
        break;
      }
      case 'waypoint':
      case 'textblock':
      case 'textlabel':
      case 'sprite':
      case 'image':
      case 'blocknode':
      case 'shape':
      case 'adr-trigger':
        validEntityIds.add(e.id);
        break;
    }
  }

  const rawGroups = rawEntities.filter(e => e.kind === 'group');
  const groupIdSet = new Set(rawGroups.map(g => g.id));
  const groups: CanvasGroup[] = rawGroups.map(e => {
    const gd = e.data as unknown as GroupData;
    const memberNodeIds: string[] = [];
    const memberGroupIds: string[] = [];
    for (const mid of gd.memberEntityIds) {
      if (groupIdSet.has(mid)) memberGroupIds.push(mid);
      else memberNodeIds.push(mid);
    }
    const validIds = new Set([...validEntityIds, ...groupIdSet]);
    return {
      id: e.id,
      label: gd.label,
      description: gd.description,
      backgroundColor: gd.backgroundColor,
      collapsed: gd.collapsed,
      collapsedX: gd.collapsedX,
      collapsedY: gd.collapsedY,
      memberEntityIds: gd.memberEntityIds.filter(id => validIds.has(id)),
      memberNodeIds: memberNodeIds.filter(id => validEntityIds.has(id)),
      memberGroupIds: memberGroupIds.filter(id => groupIdSet.has(id)),
      layerId: e.layerId,
      displayMode: gd.displayMode ?? 'invisible',
    };
  });

  const entities = rawEntities.filter(e => validEntityIds.has(e.id));

  const allMemberIds = new Set<string>();
  for (const g of groups) {
    for (const mid of g.memberEntityIds) allMemberIds.add(mid);
  }
  const validTopLevelIds = new Set([...validEntityIds, ...groupIdSet]);
  const topLevelEntityOrder: string[] = [];
  for (const e of rawEntities) {
    if (e.kind === 'connector') continue;
    if (!validTopLevelIds.has(e.id)) continue;
    if (allMemberIds.has(e.id)) continue;
    topLevelEntityOrder.push(e.id);
  }

  return { entities, groups, topLevelEntityOrder };
}
